import { useContext } from 'react';
import Head from 'next/head';

import config from 'config';
import withMetatags from 'core/hocs/withMetatags';
import {
  Conference as ConferenceType,
  ConferencePreview as ConferencePreviewType,
  ConferenceSpeaker,
} from 'types/models/Conference';
import { Employee } from 'types/models/Employee';
import { ConfigContext } from '@providers/ConfigProvider';
import { FilesContext } from '@providers/FilesProvider';
import AsideLayout from '@layouts/AsideLayout';
import Description from '@templates/Description';
import RenderInView from '@templates/RenderInView';
import ContentBox from '@components/atoms/ContentBox';
import Typography from '@components/atoms/Typography';
import BackButton from '@components/BackButton';
import Section from '@components/Section';
import SectionHeader from '@components/SectionHeader';
import DocumentsList from '@components/DocumentsList';
import ConferencePreview from '@components/ConferencePreview';
import ConferenceInfo from '@components/ConferenceInfo';
import ConferenceTags from '@components/ConferenceTags';
import ConferencesContacts from '@components/ConferencesContacts';
import { FormattedMessage } from 'utils/intl';
import getStructuredData from 'utils/getStructuredData';
import resolveImageUrl from 'utils/resolveImageUrl';
import toLowerCase from 'utils/toLowerCase';
import map from 'utils/map';

import ConferenceAdditionalInfo from './components/ConferenceAdditionalInfo';
import ConferenceSchedule from './components/ConferenceSchedule';
import ConferencePartners from './components/ConferencePartners';
import ConferenceSpeakers from './components/ConferenceSpeakers';
import ConferenceForm from './components/ConferenceForm';
import { getConferenceStructuredData } from './Conference.utils';
import messages from './Conference.messages';
import classes from './Conference.module.scss';

const { routes } = config;

type Props = {
  conference: ConferenceType;
  futureConferences: ConferencePreviewType[];
  employees: Employee[];
};

export const Conference = ({ conference, futureConferences, employees }: Props): JSX.Element => {
  const { appUrl, contact } = useContext(ConfigContext);
  const { getShopImageConfigs } = useContext(FilesContext);
  const { seoData, image } = conference;
  const content = seoData?.description;
  const metaDescription = seoData?.metaDescription;
  const speakers = map(conference.speakers, 'speaker') as ConferenceSpeaker[];
  const [companyLogo] = getShopImageConfigs(['company-logo']);
  const companyLogoUrl = companyLogo ? resolveImageUrl(companyLogo, { appUrl }) : undefined;
  const conferenceFiles = conference.files.map((file) => ({ ...file, productDocumentTypeId: 'PDF' }) as const);

  return (
    <>
      <Head>
        {seoData && (
          <>
            <title>{seoData?.title}</title>
            <meta property="og:title" content={seoData?.title} key="og-title" />
            <meta property="og:description" content={metaDescription} key="og-description" />
            <meta name="description" content={metaDescription} key="description" />
          </>
        )}
        <link rel="stylesheet" type="text/css" href="/prism.css" />
        {getStructuredData(getConferenceStructuredData(conference, { appUrl, contact, companyLogoUrl }))}
      </Head>
      <AsideLayout>
        <AsideLayout.Title>
          <div className={classes.heading}>
            <ConferenceInfo {...conference} />
            <Typography variant="huge" weight="400" renderAs="h1">
              {seoData?.header}
            </Typography>
            <ConferenceTags
              tags={conference.tags}
              shareData={{ href: routes.conferences.name.href, slug: conference.slug, seoData: conference.seoData }}
            />
          </div>
          {image && (
            <ContentBox
              className={classes.image}
              variant="image"
              alt={conference.slug}
              objectFit={toLowerCase(conference.imageFit)}
              resolveConfig={{
                preset: 'article_thumbnail_main',
                extension: image.extension,
                name: image.basename,
                productName: 'article',
                description: 'thumbnail',
                width: image.width,
                height: image.height,
              }}
              hasLazyLoad={false}
              hasPreload
            />
          )}
        </AsideLayout.Title>
        <AsideLayout.Content>
          {content && (
            <div className={classes.content}>
              <Description data={content} />
            </div>
          )}
          <Section title={<FormattedMessage {...messages.schedule} />}>
            <ConferenceSchedule items={conference.scheduleItems} speakers={speakers} />
          </Section>
          {!!conference.speakers.length && (
            <Section title={<FormattedMessage {...messages.speakers} />}>
              <ConferenceSpeakers speakers={speakers} />
            </Section>
          )}
          <Section id="register" title={<FormattedMessage {...messages.register} />}>
            <RenderInView>
              {({ ref, inView }) => (
                <div ref={ref}>
                  {inView && (
                    <ConferenceForm id={conference.id} name={seoData?.header || ''} isLocked={conference?.isLocked} />
                  )}
                </div>
              )}
            </RenderInView>
          </Section>
          {!!conference.partners.length && (
            <Section title={<FormattedMessage {...messages.partners} />}>
              <ConferencePartners partners={map(conference.partners, 'partner')} />
            </Section>
          )}
          {!!conferenceFiles.length && (
            <Section
              id="documents"
              title={<FormattedMessage {...messages.files} />}
              classes={{ root: classes.lastSection }}
            >
              <DocumentsList documents={conferenceFiles} />
            </Section>
          )}
          {!!futureConferences.length && (
            <div className={classes.conferences}>
              <SectionHeader renderAs="div">
                <FormattedMessage {...messages.future} />
              </SectionHeader>
              {futureConferences.map((futureConference) => (
                <ConferencePreview key={conference.id} variant="small" {...futureConference} withInfo />
              ))}
            </div>
          )}
        </AsideLayout.Content>
        <AsideLayout.Aside>
          {({ stickyClassName }) => <ConferenceAdditionalInfo className={stickyClassName} conference={conference} />}
        </AsideLayout.Aside>
      </AsideLayout>
      {!!employees.length && <ConferencesContacts employees={employees} />}
      <BackButton className={classes.backButton} />
    </>
  );
};

export default withMetatags(Conference, { withCanonicalUrl: true });
